// src/pages/LoginPage.js

import React, { useState } from 'react';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, QrCode2 } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LoginPage = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const formData = new URLSearchParams();
      formData.append('username', email.toLowerCase().trim());
      formData.append('password', password);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/token`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      localStorage.setItem('token', response.data.access_token);
      sessionStorage.setItem('user', JSON.stringify(response.data.user));
      
      if (response.data.user.isAdmin) {
        navigate('/admin-dashboard');
      } else {
        navigate('/form');
      }
    } catch (error) {
      console.log('Login error details:', error.response?.data); // Debug logging
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found. Please check your email address.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Invalid email format. Please enter a valid email address.';
      } else if (error.response?.status === 500) {
        // Handle 500 errors which are often authentication failures
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail.toLowerCase();
          if (detail.includes('authentication') || 
              detail.includes('failed') || 
              detail.includes('incorrect') || 
              detail.includes('invalid') || 
              detail.includes('password') || 
              detail.includes('email') || 
              detail.includes('credentials')) {
            errorMessage = 'Invalid email or password. Please check your credentials.';
          } else {
            errorMessage = 'Server error. Please try again later.';
          }
        } else {
          errorMessage = 'Invalid email or password. Please check your credentials.';
        }
      } else if (error.response?.data?.detail) {
        // Handle specific backend error messages
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          const detailLower = detail.toLowerCase();
          if (detailLower.includes('authentication') ||
              detailLower.includes('incorrect') || 
              detailLower.includes('invalid') || 
              detailLower.includes('failed') ||
              detailLower.includes('password') || 
              detailLower.includes('email') || 
              detailLower.includes('credentials')) {
            errorMessage = 'Invalid email or password. Please check your credentials.';
          } else {
            errorMessage = detail;
          }
        } else {
          errorMessage = 'Invalid email or password. Please check your credentials.';
        }
      } else if (error.message && error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Default to credentials error for any authentication-related issues
        errorMessage = 'Invalid email or password. Please check your credentials.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1F425C 0%, #2c5f7c 50%, #3a7ca3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(240, 207, 85, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(172, 30, 21, 0.05) 0%, transparent 50%),
            linear-gradient(45deg, transparent 40%, rgba(240, 207, 85, 0.02) 50%, transparent 60%)
          `,
          zIndex: 0,
        },
      }}
    >
      {/* Top Header with Hindalco Branding */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          p: 3,
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#ffffff',
          }}
        >
          <Box
            component="img"
            src="/HINDALCO.NS_BIG.png"
            alt="Hindalco Logo"
            sx={{
              height: 40,
              width: 'auto',
              mr: 2,
            }}
          />
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                color: '#ffffff',
                letterSpacing: '0.1em',
                mb: 0.5,
              }}
            >
              HINDALCO
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              Industries Limited
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            borderRadius: 3, // Rounded corners
            bgcolor: '#ffffff',
            border: 'none',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 6,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2, // Slightly rounded container
                bgcolor: '#AC1E15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0 2px 8px rgba(172, 30, 21, 0.2)',
              }}
            >
              <QrCode2 
                sx={{ 
                  fontSize: '40px', 
                  color: '#ffffff',
                }} 
              />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: '#1F425C',
                fontWeight: 300,
                textAlign: 'center',
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2rem' },
                letterSpacing: '0.025em',
              }}
            >
              QR Management System
            </Typography>
            {/* <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                textAlign: 'center',
                fontWeight: 400,
                fontSize: '0.875rem',
                mb: 3,
              }}
            >
              QR Management System
            </Typography> */}
            <Box
              sx={{
                width: 40,
                height: 2,
                bgcolor: '#F0CF55',
                mb: 2,
              }}
            />
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#1F425C',
                  mb: 1,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                Email Address
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                InputProps={{
                  style: {
                    backgroundColor: '#fff',
                    color: '#1F425C',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, // Rounded input fields
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: 1,
                    },
                    '&:hover fieldset': {
                      borderColor: '#F0CF55',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#AC1E15',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 3,
                    px: 3,
                    fontSize: '0.875rem',
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#1F425C !important',
                    color: '#1F425C !important',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
            
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#1F425C',
                  mb: 1,
                  fontWeight: 500,
                  fontSize: '0.875rem',
                }}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                InputProps={{
                  style: {
                    backgroundColor: '#fff',
                    color: '#1F425C',
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ 
                          color: '#6b7280',
                          '&:hover': {
                            color: '#AC1E15',
                            backgroundColor: 'rgba(172, 30, 21, 0.1)',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, // Rounded input fields
                    bgcolor: '#fff',
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: 1,
                    },
                    '&:hover fieldset': {
                      borderColor: '#F0CF55',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#AC1E15',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 3,
                    px: 3,
                    fontSize: '0.875rem',
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#1F425C !important',
                    color: '#1F425C !important',
                    '&::placeholder': {
                      color: '#9ca3af',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>

            {error && (
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  bgcolor: 'rgba(172, 30, 21, 0.1)',
                  border: '1px solid rgba(172, 30, 21, 0.2)',
                  borderRadius: 2, // Rounded input fields
                  borderLeft: '3px solid #AC1E15',
                }}
              >
                <Typography
                  sx={{
                    color: '#AC1E15',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 3,
                borderRadius: 2, // Rounded input fields
                bgcolor: '#AC1E15',
                color: '#ffffff',
                fontWeight: 500,
                fontSize: '0.875rem',
                textTransform: 'none',
                boxShadow: 'none',
                border: 'none',
                '&:hover': {
                  bgcolor: '#8B1612',
                  boxShadow: 'none',
                },
                '&:active': {
                  bgcolor: '#7A130F',
                },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>


        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;